
import React from 'react';
import { SimulationConfig, BaseBackgroundEffectType } from './types';
import { SectionLabel } from './SectionLabel';
import { ControlGroup } from './ControlGroup';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';

interface BackgroundSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const BackgroundSettingsSection: React.FC<BackgroundSettingsSectionProps> = ({ config, setConfig }) => {
  const backgrounds = Object.values(BaseBackgroundEffectType) as BaseBackgroundEffectType[];

  const categories = [
    { name: 'Basic', items: [BaseBackgroundEffectType.SOLID, BaseBackgroundEffectType.LINEAR_GRADIENT, BaseBackgroundEffectType.RADIAL_GRADIENT, BaseBackgroundEffectType.VIGNETTE] },
    { name: 'Animated', items: [BaseBackgroundEffectType.MESH_GRADIENT, BaseBackgroundEffectType.PLASMA, BaseBackgroundEffectType.STARFIELD, BaseBackgroundEffectType.AURORA_BASE, BaseBackgroundEffectType.DEEP_SPACE, BaseBackgroundEffectType.RGB_WAVE, BaseBackgroundEffectType.CYBER_GRID, BaseBackgroundEffectType.KALEIDOSCOPE] },
    { name: 'Textures & FX', items: [BaseBackgroundEffectType.SCANLINE, BaseBackgroundEffectType.NOISE_GRAIN, BaseBackgroundEffectType.MATRIX_CODE, BaseBackgroundEffectType.GEOMETRIC_TILES] },
    { name: 'External Media', items: [BaseBackgroundEffectType.STATIC_IMAGE, BaseBackgroundEffectType.IMAGE_BACKDROP, BaseBackgroundEffectType.VIDEO_STREAM] }
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-2 px-1">
        <SectionLabel icon="fa-fill-drip" darkMode={config.darkMode}>Base Background</SectionLabel>
        <button 
          onClick={() => {
            const types = backgrounds;
            const randomType = types[Math.floor(Math.random() * types.length)];
            setConfig(p => ({
              ...p,
              backgroundEffect: randomType,
              backgroundPrimaryColor: `#${Math.floor(Math.random()*16777215).toString(16)}`,
              backgroundSecondaryColor: `#${Math.floor(Math.random()*16777215).toString(16)}`,
              backgroundTertiaryColor: `#${Math.floor(Math.random()*16777215).toString(16)}`,
              backgroundSpeed: 0.5 + Math.random() * 2,
              backgroundOpacity: 0.5 + Math.random() * 0.5,
              backgroundComplexity: 0.5 + Math.random() * 2,
              backgroundRainbowMode: Math.random() > 0.7,
              backgroundAnimate: true
            }));
          }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase transition-all shadow-sm active:scale-95 ${
            config.darkMode 
            ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30 hover:bg-rose-500/30' 
            : 'bg-rose-100 text-rose-600 border border-rose-200 hover:bg-rose-200'
          }`}
        >
          <i className="fa-solid fa-palette" />
          Randomize BG
        </button>
      </div>
      <div className="flex flex-col gap-3 w-full p-4 rounded-3xl border bg-neutral-500/5 border-neutral-500/10 mb-2">
        <div className="flex items-center justify-between border border-white/5 p-2 rounded-xl">
          <span className="text-[10px] font-black uppercase opacity-60">Enable Background System</span>
          <Toggle active={config.backgroundEnabled} onClick={() => setConfig(p => ({...p, backgroundEnabled: !p.backgroundEnabled}))} color="bg-rose-500" />
        </div>

        {config.backgroundEnabled && (
          <>
            <div className="flex flex-col gap-4 mb-2">
                {categories.map(cat => (
                    <div key={cat.name} className="flex flex-col gap-1.5">
                        <span className="text-[8px] font-black uppercase opacity-30 ml-1 tracking-widest">{cat.name}</span>
                        <div className="grid grid-cols-2 gap-1.5 p-1.5 rounded-xl bg-black/5 border border-white/5 shadow-inner">
                            {cat.items.map(bg => (
                                <button
                                    key={bg}
                                    onClick={() => setConfig(p => ({...p, backgroundEffect: bg}))}
                                    className={`py-2 px-3 rounded-lg text-[9px] font-black uppercase transition-all text-left truncate leading-tight border ${
                                        config.backgroundEffect === bg 
                                        ? (config.darkMode ? 'bg-white text-black border-white shadow-lg' : 'bg-black text-white border-black shadow-lg') 
                                        : 'bg-white/5 text-white/40 border-transparent hover:bg-white/10 hover:text-white'
                                    }`}
                                >
                                    {bg.replace(/_/g, ' ')}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {(config.backgroundEffect === BaseBackgroundEffectType.STATIC_IMAGE || 
              config.backgroundEffect === BaseBackgroundEffectType.IMAGE_BACKDROP || 
              config.backgroundEffect === BaseBackgroundEffectType.VIDEO_STREAM) && (
              <div className="p-3 rounded-2xl border border-rose-500/20 bg-rose-500/5 flex flex-col gap-3 mb-2 shadow-inner">
                <span className="text-[9px] font-black uppercase text-rose-400">Background Media Config</span>
                
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-black uppercase opacity-60">Background Image URL</span>
                  <input 
                    type="text" 
                    placeholder="Enter Image URL"
                    value={config.backgroundImageUrl || ''} 
                    onChange={e => setConfig(p => ({...p, backgroundImageUrl: e.target.value}))}
                    className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <span className="text-[10px] font-black uppercase opacity-60">Background Video URL</span>
                  <input 
                    type="text" 
                    placeholder="Enter Video URL (MP4)"
                    value={config.backgroundVideoUrl || ''} 
                    onChange={e => setConfig(p => ({...p, backgroundVideoUrl: e.target.value}))}
                    className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`}
                  />
                </div>

                <div className="grid grid-cols-1 gap-3">
                   <ControlGroup label="Fitting Mode">
                      <select 
                        value={config.backgroundMediaFit} 
                        onChange={e => setConfig(p => ({...p, backgroundMediaFit: e.target.value as any}))}
                        className={`w-full px-2 py-1 border rounded-lg text-[10px] font-black ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`}
                      >
                        <option value="COVER">Cover (Fill)</option>
                        <option value="CONTAIN">Contain (Best Fit)</option>
                        <option value="STRETCH">Stretch</option>
                        <option value="TILE">Tile (Repeat)</option>
                      </select>
                   </ControlGroup>
                   <SmartInput label="Media Opacity" min={0} max={1} step={0.05} value={config.backgroundMediaOpacity} onChange={v => setConfig(p => ({...p, backgroundMediaOpacity: v}))} darkMode={config.darkMode} fullWidth />
                </div>
              </div>
            )}

            <div className="p-3 rounded-2xl border border-white/5 bg-white/5 flex flex-col gap-4">
               <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase opacity-60">Rainbow RGB Mode</span>
                  <Toggle active={config.backgroundRainbowMode} onClick={() => setConfig(p => ({...p, backgroundRainbowMode: !p.backgroundRainbowMode}))} />
               </div>
               
               {!config.backgroundRainbowMode && (
                 <div className="grid grid-cols-3 gap-2">
                    <div className="flex flex-col gap-1">
                        <span className="text-[8px] font-black opacity-40 uppercase">Primary</span>
                        <input type="color" value={config.backgroundPrimaryColor} onChange={e => setConfig(p => ({...p, backgroundPrimaryColor: e.target.value}))} className="w-full h-8 rounded-lg cursor-pointer bg-transparent" />
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-[8px] font-black opacity-40 uppercase">Secondary</span>
                        <input type="color" value={config.backgroundSecondaryColor} onChange={e => setConfig(p => ({...p, backgroundSecondaryColor: e.target.value}))} className="w-full h-8 rounded-lg cursor-pointer bg-transparent" />
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-[8px] font-black opacity-40 uppercase">Tertiary</span>
                        <input type="color" value={config.backgroundTertiaryColor} onChange={e => setConfig(p => ({...p, backgroundTertiaryColor: e.target.value}))} className="w-full h-8 rounded-lg cursor-pointer bg-transparent" />
                    </div>
                 </div>
               )}

               <div className="flex flex-col gap-4 mt-1">
                  <SmartInput label="Alpha / Opacity" min={0} max={1} step={0.01} value={config.backgroundOpacity} onChange={v => setConfig(p => ({...p, backgroundOpacity: v}))} darkMode={config.darkMode} fullWidth />
                  <SmartInput label="Animation Speed" min={0} max={5} step={0.1} value={config.backgroundSpeed} onChange={v => setConfig(p => ({...p, backgroundSpeed: v}))} darkMode={config.darkMode} fullWidth />
                  <SmartInput label="Complexity" min={0.1} max={5} step={0.1} value={config.backgroundComplexity} onChange={v => setConfig(p => ({...p, backgroundComplexity: v}))} darkMode={config.darkMode} fullWidth />
                  <SmartInput label="Effect Angle" min={0} max={360} value={config.backgroundAngle} onChange={v => setConfig(p => ({...p, backgroundAngle: v}))} darkMode={config.darkMode} fullWidth />
               </div>

               <div className="h-px bg-white/5 my-1" />
               <span className="text-[8px] font-black uppercase opacity-40">Filter Overrides</span>
               <div className="flex flex-col gap-4">
                  <SmartInput label="Brightness" min={0} max={2} step={0.05} value={config.backgroundBrightness} onChange={v => setConfig(p => ({...p, backgroundBrightness: v}))} darkMode={config.darkMode} fullWidth />
                  <SmartInput label="Contrast" min={0} max={2} step={0.05} value={config.backgroundContrast} onChange={v => setConfig(p => ({...p, backgroundContrast: v}))} darkMode={config.darkMode} fullWidth />
                  <SmartInput label="Saturation" min={0} max={2} step={0.05} value={config.backgroundSaturation} onChange={v => setConfig(p => ({...p, backgroundSaturation: v}))} darkMode={config.darkMode} fullWidth />
               </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
